import React, { useState, useEffect, useCallback } from "react";
import {
  collection,
  query,
  where,
  addDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
  runTransaction,
} from "firebase/firestore";
import { db } from "./firebase-config";
import {
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Checkbox,
  Snackbar,
  Alert,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  FormControlLabel,
} from "@mui/material";
import { Gauge } from "@mui/x-charts/Gauge";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import BathtubIcon from "@mui/icons-material/BathtubOutlined";
import NoPhotographyIcon from "@mui/icons-material/NoPhotography";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const Students = ({ isAdmin, userRoles = [] }) => {
  const canEdit = isAdmin || (userRoles && userRoles.includes("principal"));
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [classFilter, setClassFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [classes, setClasses] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({});
  const [isBirthDateValid, setIsBirthDateValid] = useState(true);
  const [isBaptismDateValid, setIsBaptismDateValid] = useState(true);
  const [isFormValid, setIsFormValid] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const schoolGrades = [
    "Prep",
    "Grade 1",
    "Grade 2",
    "Grade 3",
    "Grade 4",
    "Grade 5",
    "Grade 6",
    "Grade 7",
    "Grade 8",
    "Grade 9",
    "Grade 10",
    "Grade 11",
    "Grade 12",
    "Pass Out",
  ];
  const currentYear = dayjs().year().toString();

  const fetchStudents = useCallback(async () => {
    try {
      // Fetch all students
      const studentSnapshot = await getDocs(collection(db, "students"));
      const students = studentSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Fetch all attendance records for the current year using the ID
      const attendanceQuery = query(
        collection(db, "attendance"),
        where("__name__", ">=", `${currentYear}-01-01`),
        where("__name__", "<=", `${currentYear}-12-31`),
      );
      const attendanceSnapshot = await getDocs(attendanceQuery);
      const attendanceRecords = attendanceSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Process attendance data to calculate absences for each student
      const studentData = students.map((student) => {
        const absences = attendanceRecords.filter(
          (record) => record.records[student.regNo] === false,
        );
        const nrAbsences = absences.length;
        const absenceIds = absences.map((absence) => absence.id);
        const absenceIdsString = absenceIds.join(", ");
        return { ...student, nrAbsences, absenceIdsString };
      });

      setStudents(studentData);
      setFilteredStudents(studentData);
    } catch (error) {
      console.error("Error fetching students: ", error);
    }
  }, [currentYear]);

  const fetchClasses = useCallback(async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "classes"));
      const classesList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name,
        grade: doc.data().grade,
      }));

      // Sort by length of `grade`, and then by `name`
      classesList.sort((a, b) => {
        if (a.grade.length === b.grade.length) {
          return a.name.localeCompare(b.name); // Alphabetical order
        }
        return a.grade.length - b.grade.length; // By grade length
      });

      setClasses(classesList);
    } catch (error) {
      console.error("Error fetching classes: ", error);
    }
  }, []);

  useEffect(() => {
    fetchStudents();
    fetchClasses();
  }, [fetchStudents, fetchClasses]);

  useEffect(() => {
    let filtered = students;

    if (statusFilter !== "ALL") {
      filtered = filtered.filter((student) => student.status === statusFilter);
    }

    if (classFilter !== "ALL") {
      filtered = filtered.filter((student) => student.class === classFilter);
    }

    if (searchTerm) {
      const lowercasedSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter((student) => {
        const fullName =
          `${student.firstName} ${student.middleName} ${student.lastName}`.toLowerCase();
        const fatherName = student.fatherName?.toLowerCase() || "";
        const motherName = student.motherName?.toLowerCase() || "";
        const regNo = student.regNo?.toLowerCase() || "";
        return (
          fullName.includes(lowercasedSearchTerm) ||
          fatherName.includes(lowercasedSearchTerm) ||
          motherName.includes(lowercasedSearchTerm) ||
          regNo.includes(lowercasedSearchTerm)
        );
      });
    }

    setFilteredStudents(filtered);
  }, [statusFilter, classFilter, searchTerm, students]);

  const handleFeesPaidChange = async (studentId, checked) => {
    if (!canEdit) {
      setSnackbarMessage("You do not have permission to update fees status.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    try {
      const studentRef = doc(db, "students", studentId);
      await updateDoc(studentRef, { feesPaid: checked });
      if (checked) {
        // Get studentData based on studentId
        const studentData = students.find(
          (student) => student.id === studentId,
        );
        const parentEmails = [
          studentData.fatherEmail,
          studentData.motherEmail,
          studentData.guardianEmail,
        ].filter(Boolean); // Filter out any undefined or null values
        const uniqueParentEmails = [...new Set(parentEmails)]; // Remove duplicates
        const firstName = studentData.firstName || "";
        const mailDocRef = doc(collection(db, "mail"));
        await setDoc(mailDocRef, {
          to: uniqueParentEmails,
          message: {
            subject:
              "Contribution Received - St Marys Syro Malabar Church Sunshine Coast",
            html: `Dear Parent,<br> This mail is to confirm that we have received your contribution for your child <b>${firstName}</b> towards the ${currentYear} catechism.<br><br>Kind Regards,<br>Catechism Dept.<br>St Marys Syro Malabar Mission, Sunshine Coast`,
          },
        });
      }
      setSnackbarMessage("Fees status updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      fetchStudents();
    } catch (error) {
      console.error("Error updating fees status: ", error);
      setSnackbarMessage("Failed to update fees status.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleStatusChange = async (studentId, newStatus) => {
    if (!canEdit) {
      setSnackbarMessage("You do not have permission to update status.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    try {
      const studentRef = doc(db, "students", studentId);
      const updates = { status: newStatus };

      if (newStatus !== "Active") {
        updates.class = "";
        updates.classYear = null;
      }

      await updateDoc(studentRef, updates);
      setSnackbarMessage("Status updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      fetchStudents();
    } catch (error) {
      console.error("Error updating status: ", error);
      setSnackbarMessage("Failed to update status.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "homePostcode") {
      // Allow only integers between 100 and 9999
      const intValue = parseInt(value, 10);
      if (intValue >= 100 && intValue <= 9999) {
        // Format to always show 4 digits
        const formattedValue = String(intValue).padStart(4, "0");
        setFormData((prevData) => ({
          ...prevData,
          [name]: formattedValue,
        }));
      }
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: type === "checkbox" ? checked : value,
      }));

      if (
        name === "hasAllergicReactionAsthmaAnaphylaxis" &&
        value === true &&
        !formData.allergyNotes
      ) {
        setIsFormValid(false);
        setSnackbarMessage("Enter details in Allergy Notes");
        setSnackbarOpen(true);
      } else if (
        name === "allergyNotes" &&
        formData.hasAllergicReactionAsthmaAnaphylaxis &&
        !value
      ) {
        setIsFormValid(false);
      } else {
        setIsFormValid(true);
      }
    }
  };

  const handleDateChange = (name, value) => {
    const today = dayjs();
    if (name === "birthDate" && value.isAfter(today)) {
      setSnackbarMessage("Birth date cannot be in the future.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setIsBirthDateValid(false);
      setIsFormValid(false);
      return;
    }
    if (
      name === "baptismDate" &&
      (value.isBefore(formData.birthDate) || value.isAfter(today))
    ) {
      setSnackbarMessage("Baptism date must be between birth date and today.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setIsBaptismDateValid(false);
      setIsFormValid(false);
      return;
    }
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    setIsBirthDateValid(true);
    setIsBaptismDateValid(true);
    setIsFormValid(true);
  };

  const handleSave = async () => {
    if (!canEdit) {
      setSnackbarMessage("You do not have permission to save details.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    if (!formData.firstName || !formData.lastName || !formData.gender || !formData.schoolGrade) {
      setSnackbarMessage("Please fill in all required fields.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      let studentData = {
        ...formData,
        schoolGradeYear: dayjs().year(),
        classYear: formData.class === "" ? null : dayjs().year(),
        status: formData.schoolGrade === "Pass Out" ? "Pass Out" : "Active",
      };
      if (
        studentData.parishFamilyRegNo &&
        studentData.parishFamilyRegNo !== "NA" &&
        !/^SMSC(?!000)\d{3}$/.test(studentData.parishFamilyRegNo)
      ) {
        setSnackbarMessage(
          "Parish Family Reg No must be in format SMSC followed by 3 digits (001-999).",
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }
      // Format the birthDate and baptismDate to YYYY-MM-DD string
      studentData.birthDate =
        !studentData.birthDate || studentData.birthDate.isSame(dayjs(), "day")
          ? ""
          : studentData.birthDate.format("YYYY-MM-DD");

      studentData.baptismDate =
        !studentData.baptismDate ||
          studentData.baptismDate.isSame(dayjs(), "day")
          ? ""
          : studentData.baptismDate.format("YYYY-MM-DD");

      if (editId) {
        // Update existing student
        await updateDoc(doc(db, "students", editId), studentData);
        setSnackbarMessage("Student's details updated successfully!");
        setStudents((prevStudents) =>
          prevStudents.map((student) =>
            student.id === editId ? { ...student, ...studentData } : student,
          ),
        );
      } else {
        // Generate regNo for new student using transaction for atomicity
        const currentYear = dayjs().year();
        const counterDocRef = doc(db, "counters", `regNo-${currentYear}`);

        const newRegNo = await runTransaction(db, async (transaction) => {
          const counterDoc = await transaction.get(counterDocRef);
          let nextNumber = 1;

          if (counterDoc.exists()) {
            nextNumber = counterDoc.data().count + 1;
          }

          transaction.set(
            counterDocRef,
            { count: nextNumber },
            { merge: true },
          );

          return `SMSC-${currentYear}-${String(nextNumber).padStart(3, "0")}`;
        });

        studentData.regNo = newRegNo;

        // Add new student
        const newDocRef = await addDoc(collection(db, "students"), studentData);
        setSnackbarMessage("Student added successfully!");

        // Add the new student to the state
        setStudents((prevKids) => [
          ...prevKids,
          { id: newDocRef.id, ...studentData },
        ]);
      }
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setOpen(false);
      setFormData({
        firstName: "",
        middleName: "",
        lastName: "",
        gender: "",
        schoolGrade: "",
        class: "",
        foodAllergies: "",
        hasAllergicReactionAsthmaAnaphylaxis: false,
        allergyNotes: "",
        mediaConsent: true,
        fatherName: "",
        fatherHouseName: "",
        fatherParishIndia: "",
        fatherDioceseIndia: "",
        fatherEmail: "",
        fatherPhoneNo: "",
        motherName: "",
        motherHouseName: "",
        motherEmail: "",
        motherPhoneNo: "",
        siblingsDetails: "",
        homeAddress: "",
        homeSuburb: "",
        homePostcode: "",
        emergencyContactName: "",
        emergencyContactPhoneNo: "",
        birthDate: dayjs(),
        baptismName: "",
        baptismDate: dayjs(),
        guardianEmail: "",
        parishFamilyRegNo: "",
        principalComments: "",
        regNo: "",
      });
      setEditId(null);
      setIsFormValid(true);
    } catch (error) {
      console.error("Error saving Student's details: ", error);
      setSnackbarMessage("Failed to save details.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleEdit = (kid) => {
    setFormData({
      firstName: kid.firstName,
      middleName: kid.middleName || "",
      lastName: kid.lastName,
      gender: kid.gender || "",
      schoolGrade: kid.schoolGrade || "",
      class: kid.class || "",
      foodAllergies: kid.foodAllergies || "",
      hasAllergicReactionAsthmaAnaphylaxis:
        kid.hasAllergicReactionAsthmaAnaphylaxis,
      allergyNotes: kid.allergyNotes || "",
      mediaConsent: kid.mediaConsent,
      fatherName: kid.fatherName || "",
      fatherHouseName: kid.fatherHouseName || "",
      fatherParishIndia: kid.fatherParishIndia || "",
      fatherDioceseIndia: kid.fatherDioceseIndia || "",
      fatherEmail: kid.fatherEmail || "",
      fatherPhoneNo: kid.fatherPhoneNo || "",
      motherName: kid.motherName || "",
      motherHouseName: kid.motherHouseName || "",
      motherEmail: kid.motherEmail || "",
      motherPhoneNo: kid.motherPhoneNo || "",
      siblingsDetails: kid.siblingsDetails || "",
      homeAddress: kid.homeAddress || "",
      homeSuburb: kid.homeSuburb || "",
      homePostcode: kid.homePostcode || "",
      emergencyContactName: kid.emergencyContactName || "",
      emergencyContactPhoneNo: kid.emergencyContactPhoneNo || "",
      birthDate: kid.birthDate ? dayjs(kid.birthDate) : dayjs(),
      baptismName: kid.baptismName || "",
      baptismDate: kid.baptismDate ? dayjs(kid.baptismDate) : dayjs(),
      guardianEmail: kid.guardianEmail || "",
      parishFamilyRegNo: kid.parishFamilyRegNo || "",
      principalComments: kid.principalComments || "",
      regNo: kid.regNo || "",
    });
    setEditId(kid.id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({
      firstName: "",
      middleName: "",
      lastName: "",
      gender: "",
      schoolGrade: "",
      class: "",
      foodAllergies: "",
      hasAllergicReactionAsthmaAnaphylaxis: false,
      allergyNotes: "",
      mediaConsent: true,
      fatherName: "",
      fatherHouseName: "",
      fatherParishIndia: "",
      fatherDioceseIndia: "",
      fatherEmail: "",
      fatherPhoneNo: "",
      motherName: "",
      motherHouseName: "",
      motherEmail: "",
      motherPhoneNo: "",
      siblingsDetails: "",
      homeAddress: "",
      homeSuburb: "",
      homePostcode: "",
      emergencyContactName: "",
      emergencyContactPhoneNo: "",
      birthDate: dayjs(),
      baptismName: "",
      baptismDate: dayjs(),
      guardianEmail: "",
      parishFamilyRegNo: "",
      principalComments: "",
      regNo: "",
    });
    setEditId(null);
    setIsFormValid(true);
  };

  const handleDelete = (student) => {
    if (!isAdmin) return;
    setStudentToDelete(student);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteDoc(doc(db, "students", studentToDelete.id));
      setStudents((prevStudents) =>
        prevStudents.filter((student) => student.id !== studentToDelete.id),
      );
      setSnackbarMessage("Student deleted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error deleting student: ", error);
      setSnackbarMessage("Failed to delete student.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setDeleteDialogOpen(false);
      setStudentToDelete(null);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setStudentToDelete(null);
  };

  const handleAbsenceButtonClick = (absenceIdsString) => {
    setSnackbarMessage(absenceIdsString || "No absences recorded");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const classesNotAssignedCount = filteredStudents.filter(
    (student) => !student.class,
  ).length;
  const feesNotPaidCount = filteredStudents.filter(
    (student) => !student.feesPaid,
  ).length;
  const noBaptismDateCount = filteredStudents.filter(
    (student) => !student.baptismDate,
  ).length;
  const noAbsencesCount = filteredStudents.filter(
    (student) => student.nrAbsences === 0,
  ).length;

  return (
    <Container maxWidth="lg" className="app-wrapper">
      <Typography align="center" gutterBottom>
        Students
      </Typography>
      <TextField
        id="search-textfield"
        label="Search by RegNo Name or Parent Name"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        margin="normal"
        fullWidth
      />
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={2}
      >
        <Stack direction="column" alignItems="center">
          <Typography>No Class</Typography>
          <Gauge
            id="gauge-no-class"
            width={70}
            height={70}
            value={classesNotAssignedCount}
          />
        </Stack>
        <Stack direction="column" alignItems="center">
          <Typography>No Fees</Typography>
          <Gauge
            id="gauge-no-fees"
            width={70}
            height={70}
            value={feesNotPaidCount}
          />
        </Stack>
        <Stack direction="column" alignItems="center">
          <Typography>No Baptism</Typography>
          <Gauge
            id="gauge-no-baptism"
            width={70}
            height={70}
            value={noBaptismDateCount}
          />
        </Stack>
        <Stack direction="column" alignItems="center">
          <Typography>No Absences</Typography>
          <Gauge
            id="gauge-no-absences"
            width={70}
            height={70}
            value={noAbsencesCount}
          />
        </Stack>
      </Stack>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Parents</TableCell>
              <TableCell>
                <FormControl
                  id="class-filter-form-control"
                  fullWidth
                  margin="normal"
                >
                  <InputLabel id="class-filter-label">Class</InputLabel>
                  <Select
                    id="class-filter-select"
                    labelId="class-filter-label"
                    value={classFilter}
                    onChange={(e) => setClassFilter(e.target.value)}
                  >
                    <MenuItem id="class-menu-item-all" value="ALL">
                      <em>ALL</em>
                    </MenuItem>
                    <MenuItem id="class-menu-item-none" value="">
                      <em>None</em>
                    </MenuItem>
                    {classes.map((cls) => (
                      <MenuItem
                        id={`class-menu-item-${cls.id}`}
                        key={cls.id}
                        value={cls.name}
                      >
                        {cls.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell>Absences</TableCell>
              <TableCell>Fees Paid</TableCell>
              <TableCell>
                <FormControl
                  id="status-filter-form-control"
                  fullWidth
                  margin="normal"
                >
                  <InputLabel id="status-filter-label">Status</InputLabel>
                  <Select
                    id="status-filter-select"
                    labelId="status-filter-label"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem id="status-filter-active" value="Active">
                      Active
                    </MenuItem>
                    <MenuItem id="status-filter-pass-out" value="Pass Out">
                      Pass Out
                    </MenuItem>
                    <MenuItem
                      id="status-filter-transferred"
                      value="Transferred"
                    >
                      Transferred
                    </MenuItem>
                    <MenuItem
                      id="status-filter-discontinued"
                      value="Discontinued"
                    >
                      Discontinued
                    </MenuItem>
                    <MenuItem id="status-filter-duplicate" value="Duplicate">
                      Duplicate
                    </MenuItem>
                    <MenuItem id="status-filter-all" value="ALL">
                      ALL
                    </MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell>
                Actions
                {canEdit && (
                  <Tooltip title="Add a new Student">
                    <IconButton
                      id="add-student-button"
                      variant="contained"
                      color="primary"
                      onClick={() => setOpen(true)}
                    >
                      <AddCircleOutlineIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredStudents.map((student) => (
              <TableRow id={`student-${student.id}`} key={student.id}>
                <TableCell>
                  {student.firstName} {student.middleName} {student.lastName}
                  {!student.baptismDate && (
                    <BathtubIcon style={{ color: "red", marginLeft: 4 }} />
                  )}
                  {!student.mediaConsent && (
                    <NoPhotographyIcon
                      style={{ color: "red", marginLeft: 4 }}
                    />
                  )}
                  <Typography variant="body2" color="textSecondary">
                    {student.regNo ? student.regNo : ""}
                  </Typography>
                </TableCell>
                <TableCell>
                  {student.fatherName}, {student.motherName}
                </TableCell>
                <TableCell>{student.class}</TableCell>
                <TableCell>
                  <Tooltip title={student.absenceIdsString}>
                    <Button
                      id={`absence-button-${student.id}`}
                      onClick={() =>
                        handleAbsenceButtonClick(student.absenceIdsString)
                      }
                    >
                      {student.nrAbsences}
                    </Button>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Checkbox
                    id={`fees-paid-checkbox-${student.id}`}
                    checked={student.feesPaid || false}
                    disabled={!canEdit}
                    onChange={(e) =>
                      handleFeesPaidChange(student.id, e.target.checked)
                    }
                  />
                </TableCell>
                <TableCell>
                  <FormControl
                    id={`status-form-control-${student.id}`}
                    fullWidth
                  >
                    <Select
                      id={`status-select-${student.id}`}
                      value={student.status}
                      disabled={!canEdit}
                      onChange={(e) =>
                        handleStatusChange(student.id, e.target.value)
                      }
                    >
                      <MenuItem
                        id={`status-menu-item-active-${student.id}`}
                        value="Active"
                      >
                        Active
                      </MenuItem>
                      <MenuItem
                        id={`status-menu-item-pass-out-${student.id}`}
                        value="Pass Out"
                      >
                        Pass Out
                      </MenuItem>
                      <MenuItem
                        id={`status-menu-item-transferred-${student.id}`}
                        value="Transferred"
                      >
                        Transferred
                      </MenuItem>
                      <MenuItem
                        id={`status-menu-item-discontinued-${student.id}`}
                        value="Discontinued"
                      >
                        Discontinued
                      </MenuItem>
                      <MenuItem
                        id={`status-menu-item-duplicate-${student.id}`}
                        value="Duplicate"
                      >
                        Duplicate
                      </MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  {canEdit && (
                    <Tooltip title="Edit Student details">
                      <IconButton
                        id={`edit-student-button-${student.id}`}
                        variant="contained"
                        color="primary"
                        onClick={() => handleEdit(student)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {isAdmin && (
                    <Tooltip title="Delete Student">
                      <IconButton
                        id={`delete-student-button-${student.id}`}
                        variant="contained"
                        color="secondary"
                        onClick={() => handleDelete(student)}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editId ? "Edit Student" : "Add Student"}</DialogTitle>
        <DialogContent>
          {isAdmin && editId && (
            <TextField
              id="dialog-reg-no-textfield"
              margin="dense"
              label="Registration No"
              name="regNo"
              fullWidth
              value={formData.regNo || ""}
              onChange={handleChange}
            />
          )}
          <FormControl
            id="dialog-first-name-form-control"
            fullWidth
            margin="dense"
            error={!formData.firstName}
          >
            <TextField
              id="dialog-first-name-textfield"
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </FormControl>
          <TextField
            id="dialog-middle-name-textfield"
            margin="dense"
            label="Middle Name"
            name="middleName"
            fullWidth
            value={formData.middleName}
            onChange={handleChange}
          />
          <FormControl
            id="dialog-last-name-form-control"
            fullWidth
            margin="dense"
            error={!formData.lastName}
          >
            <TextField
              id="dialog-last-name-textfield"
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </FormControl>
          <TextField
            id="dialog-guardian-email-textfield"
            margin="dense"
            label="Guardian Email"
            name="guardianEmail"
            fullWidth
            value={formData.guardianEmail}
            onChange={handleChange}
          />
          <TextField
            id="dialog-parish-family-regno-textfield"
            margin="dense"
            label="Parish Family Reg No."
            name="parishFamilyRegNo"
            fullWidth
            value={formData.parishFamilyRegNo}
            onChange={handleChange}
            helperText="Format: SMSC followed by 3 digits (001-999) or 'NA'."
            error={formData.parishFamilyRegNo && formData.parishFamilyRegNo !== "NA" && !/^SMSC(?!000)\d{3}$/.test(formData.parishFamilyRegNo)}
          />
          <TextField
            id="dialog-principal-comments-textfield"
            margin="dense"
            label="Principal Comments"
            name="principalComments"
            fullWidth
            multiline
            rows={3}
            value={formData.principalComments || ""}
            onChange={handleChange}
          />
          <FormControl id="dialog-gender-form-control" fullWidth margin="dense" error={!formData.gender}>
            <InputLabel id="gender-label">Gender</InputLabel>
            <Select
              id="dialog-gender-select"
              labelId="gender-label"
              label="Gender"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
            >
              <MenuItem id="dialog-gender-blank" key="" value=""></MenuItem>
              <MenuItem id="dialog-gender-male" key="Male" value="Male">
                Male
              </MenuItem>
              <MenuItem id="dialog-gender-female" key="Female" value="Female">
                Female
              </MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                id="dialog-fees-paid-checkbox"
                checked={formData.feesPaid || false}
                onChange={handleChange}
                name="feesPaid"
              />
            }
            label="Fees Paid"
          />
          <FormControl
            id="dialog-school-grade-form-control"
            fullWidth
            margin="normal"
            error={!formData.schoolGrade}
          >
            <InputLabel id="dialog-school-grade-label">School Grade</InputLabel>
            <Select
              id="dialog-school-grade-select"
              labelId="dialog-school-grade-label"
              label="School Grade"
              name="schoolGrade"
              value={formData.schoolGrade}
              onChange={handleChange}
              required
            >
              {schoolGrades.map((schoolGrade) => (
                <MenuItem
                  id={`dialog-school-grade-menu-item-${schoolGrade.replace(
                    /\s+/g,
                    "-",
                  )}`}
                  key={schoolGrade}
                  value={schoolGrade}
                >
                  {schoolGrade}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl id="dialog-class-form-control" fullWidth margin="normal">
            <InputLabel id="class-label">Class</InputLabel>
            <Select
              id="dialog-class-select"
              labelId="class-label"
              label="Class"
              name="class"
              value={formData.class}
              onChange={handleChange}
              required
            >
              {classes.map((classItem) => (
                <MenuItem
                  id={`dialog-class-menu-item-${classItem.id}`}
                  key={classItem.id}
                  value={classItem.name}
                >
                  {classItem.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              id="dialog-birth-date-datepicker"
              label="Date of Birth"
              value={formData.birthDate}
              name="birthDate"
              onChange={(newValue) => handleDateChange("birthDate", newValue)}
              format="DD-MM-YYYY"
              inputFormat="YYYY-MM-DD"
              fullWidth
              error={!isBirthDateValid}
              helperText={
                !isBirthDateValid && "Please enter valid Date of Birth."
              }
            />
            <TextField
              id="dialog-baptism-name-textfield"
              margin="dense"
              label="Baptism Name"
              name="baptismName"
              fullWidth
              value={formData.baptismName}
              onChange={handleChange}
            />
            <DatePicker
              id="dialog-baptism-date-datepicker"
              label="Date of Baptism"
              value={formData.baptismDate}
              name="baptismDate"
              onChange={(newValue) => handleDateChange("baptismDate", newValue)}
              format="DD-MM-YYYY"
              inputFormat="YYYY-MM-DD"
              fullWidth
              error={!isBaptismDateValid}
              helperText={
                !isBaptismDateValid && "Please enter valid Date of Baptism."
              }
            />
            <TextField
              id="dialog-food-allergies-textfield"
              margin="dense"
              label="Food Allergies"
              name="foodAllergies"
              fullWidth
              value={formData.foodAllergies}
              onChange={handleChange}
            />
            <FormControl
              id="dialog-has-allergic-reaction-form-control"
              fullWidth
              margin="normal"
            >
              <InputLabel id="dialog-has-allergic-reaction-label">
                Has Allergic reaction, Asthma, or Anaphylaxis
              </InputLabel>
              <Select
                id="dialog-has-allergic-reaction-select"
                labelId="dialog-has-allergic-reaction-label"
                value={formData.hasAllergicReactionAsthmaAnaphylaxis}
                onChange={handleChange}
                name="hasAllergicReactionAsthmaAnaphylaxis"
              >
                <MenuItem
                  id="dialog-has-allergic-reaction-true"
                  key={true}
                  value={true}
                >
                  Yes
                </MenuItem>
                <MenuItem
                  id="dialog-has-allergic-reaction-false"
                  key={false}
                  value={false}
                >
                  No
                </MenuItem>
              </Select>
            </FormControl>
            <TextField
              id="dialog-allergy-notes-textfield"
              margin="dense"
              label="Allergy Notes"
              name="allergyNotes"
              fullWidth
              value={formData.allergyNotes}
              onChange={handleChange}
              required={formData.hasAllergicReactionAsthmaAnaphylaxis}
            />
            <FormControl
              id="dialog-media-consent-form-control"
              fullWidth
              margin="normal"
            >
              <InputLabel id="dialog-media-consent-label">
                Media consent given for photos/videos
              </InputLabel>
              <Select
                id="dialog-media-consent-select"
                labelId="dialog-media-consent-label"
                value={formData.mediaConsent}
                onChange={handleChange}
                name="mediaConsent"
              >
                <MenuItem
                  id="dialog-media-consent-true"
                  key={true}
                  value={true}
                >
                  Yes
                </MenuItem>
                <MenuItem
                  id="dialog-media-consent-false"
                  key={false}
                  value={false}
                >
                  No
                </MenuItem>
              </Select>
            </FormControl>
          </LocalizationProvider>
          <TextField
            id="dialog-father-name-textfield"
            margin="dense"
            label="Father's Name"
            name="fatherName"
            fullWidth
            value={formData.fatherName}
            onChange={handleChange}
          />
          <TextField
            id="dialog-father-house-name-textfield"
            margin="dense"
            label="Father's House Name"
            name="fatherHouseName"
            fullWidth
            value={formData.fatherHouseName}
            onChange={handleChange}
          />
          <TextField
            id="dialog-father-email-textfield"
            margin="dense"
            label="Father's Email"
            name="fatherEmail"
            fullWidth
            value={formData.fatherEmail}
            onChange={handleChange}
          />
          <TextField
            id="dialog-father-phone-no-textfield"
            margin="dense"
            label="Father's Phone Number"
            name="fatherPhoneNo"
            fullWidth
            value={formData.fatherPhoneNo}
            onChange={handleChange}
          />
          <TextField
            id="dialog-mother-name-textfield"
            margin="dense"
            label="Mother's Name"
            name="motherName"
            fullWidth
            value={formData.motherName}
            onChange={handleChange}
          />
          <TextField
            id="dialog-mother-email-textfield"
            margin="dense"
            label="Mother's Email"
            name="motherEmail"
            fullWidth
            value={formData.motherEmail}
            onChange={handleChange}
          />
          <TextField
            id="dialog-mother-phone-no-textfield"
            margin="dense"
            label="Mother's Phone Number"
            name="motherPhoneNo"
            fullWidth
            value={formData.motherPhoneNo}
            onChange={handleChange}
          />
          <TextField
            id="dialog-home-address-textfield"
            margin="dense"
            label="Home Address"
            name="homeAddress"
            fullWidth
            value={formData.homeAddress}
            onChange={handleChange}
          />
          <TextField
            id="dialog-home-suburb-textfield"
            margin="dense"
            label="Home Suburb"
            name="homeSuburb"
            fullWidth
            value={formData.homeSuburb}
            onChange={handleChange}
          />
          <TextField
            id="dialog-home-postcode-textfield"
            margin="dense"
            label="Home Postcode"
            name="homePostcode"
            fullWidth
            value={formData.homePostcode}
            onChange={handleChange}
          />
          <TextField
            id="dialog-siblings-details-textfield"
            margin="dense"
            label="Siblings Details"
            name="siblingsDetails"
            fullWidth
            value={formData.siblingsDetails}
            onChange={handleChange}
          />
          <TextField
            id="dialog-emergency-contact-name-textfield"
            margin="dense"
            label="Emergency Contact Name"
            name="emergencyContactName"
            fullWidth
            value={formData.emergencyContactName}
            onChange={handleChange}
          />
          <TextField
            id="dialog-emergency-contact-phone-no-textfield"
            margin="dense"
            label="Emergency Contact Phone Number"
            name="emergencyContactPhoneNo"
            fullWidth
            value={formData.emergencyContactPhoneNo}
            onChange={handleChange}
          />
          <TextField
            id="dialog-father-parish-india-textfield"
            margin="dense"
            label="Father's Parish (India)"
            name="fatherParishIndia"
            fullWidth
            value={formData.fatherParishIndia}
            onChange={handleChange}
          />
          <TextField
            id="dialog-father-diocese-india-textfield"
            margin="dense"
            label="Father's Diocese (India)"
            name="fatherDioceseIndia"
            fullWidth
            value={formData.fatherDioceseIndia}
            onChange={handleChange}
          />
        </DialogContent>
        <DialogActions>
          <Button
            id="dialog-button-close"
            onClick={handleClose}
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            id="dialog-button-save"
            onClick={handleSave}
            color="primary"
            disabled={!isFormValid}
          >
            {editId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Student</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this student?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            id="delete-dialog-button-close"
            onClick={handleCloseDeleteDialog}
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            id="delete-dalog-button-confirm-delete"
            onClick={handleConfirmDelete}
            color="primary"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Students;
